"use client"
import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react"

interface ExternalApiFormStoreData {
  [key: string]: any
}

interface ExternalApiFormStoreContextType {
  data: ExternalApiFormStoreData
  setData: React.Dispatch<React.SetStateAction<ExternalApiFormStoreData>>
}

const ExternalApiFormStoreContext = createContext<
  ExternalApiFormStoreContextType | undefined
>(undefined)

interface ExternalApiFormStoreProviderProps {
  children: ReactNode
  initialData?: ExternalApiFormStoreData
}

export const ExternalApiFormStoreProvider: React.FC<
  ExternalApiFormStoreProviderProps
> = ({ children, initialData = {} }) => {
  const [data, setData] = useState<ExternalApiFormStoreData>(initialData)
  const [isMounted, setIsMounted] = useState(false)

  useEffect(() => {
    setIsMounted(true)
  }, [])

  // Always provide context value, even during SSR/hydration
  return (
    <ExternalApiFormStoreContext.Provider value={{ data, setData }}>
      {children}
    </ExternalApiFormStoreContext.Provider>
  )
}

export const useExternalApiFormStore = (): ExternalApiFormStoreContextType => {
  const context = useContext(ExternalApiFormStoreContext)
  if (context === undefined) {
    // During SSR/SSG, provide a fallback instead of throwing an error
    if (typeof window === "undefined") {
      return {
        data: {},
        setData: () => {},
      }
    }
    console.error(
      "useExternalApiFormStore must be used within an ExternalApiFormStoreProvider",
    )
    return { data: {}, setData: () => {} }
  }
  return context
}
